import type { VinDetail } from '@/api/schemas';
import { GHOST_BUTTON_LABEL_CLASS } from '@/components/BackButton';
import { Field, LABEL_CLASS, VALUE_STRONG_CLASS } from '@/components/Field';
import { IntlIcon } from '@/components/IntlIcon';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import { SectionHeading } from '@/components/ui/section-heading';
import { NOT_AVAILABLE, orNotAvailable } from '@/lib/display';
import { DateText } from '@/lib/formatDate';

function PurchaseInformation({ detail }: { detail: VinDetail | null }) {
  return (
    <Panel variant="data">
      <div className="grid h-full grid-cols-2 gap-x-6 gap-y-2">
        <Field
          label="Current Owner"
          value={orNotAvailable(detail?.customerName)}
          caption={
            detail?.customerLastChangedDate ? (
              <>
                as of <DateText value={detail.customerLastChangedDate} />
              </>
            ) : undefined
          }
        />
        <Field
          label="Sell Location"
          value={orNotAvailable(detail?.sellingDealerLocation)}
          strong={false}
        />
        <Field
          label="Dealer Account #"
          value={orNotAvailable(detail?.sellingDealerAccount)}
          strong={false}
        />
        <Field label="Registration" value={NOT_AVAILABLE} strong={false} />
        <Field label="Factory Order #" value={NOT_AVAILABLE} strong={false} />
      </div>
    </Panel>
  );
}

function VehicleInformation({
  detail,
  vin,
  onMoreDetails,
}: {
  detail: VinDetail | null;
  vin: string;
  onMoreDetails: () => void;
}) {
  return (
    <Panel variant="data" size="flush">
      <div className="grid grid-cols-3 gap-x-6 gap-y-2 p-4">
        <Field label="VIN" value={detail?.vin ?? vin} />
        <div>
          <p className={LABEL_CLASS}>Make/Model</p>
          <p className={`mt-1 ${VALUE_STRONG_CLASS}`}>
            {detail?.make || NOT_AVAILABLE}
          </p>
          <p className={VALUE_STRONG_CLASS}>
            {detail?.modelFamily || NOT_AVAILABLE}
          </p>
        </div>
        <Field label="Year" value={orNotAvailable(detail?.modelYear)} />
        <Field label="Condition" value={NOT_AVAILABLE} />
        <Field
          label="Engine Info"
          value={orNotAvailable(detail?.engineSerialNumber)}
          caption={detail?.horsepower ? `${detail.horsepower} HP` : undefined}
        />
        <Field label="State Of Health" value={NOT_AVAILABLE} />
        <Field
          label="DTU - Deliver to User"
          value={
            detail?.deliveryDate ? (
              <DateText value={detail.deliveryDate} />
            ) : (
              NOT_AVAILABLE
            )
          }
          strong={false}
        />
        <Field
          label="Last Recorded Mileage"
          value={
            detail?.lastRecordedOdometer != null
              ? `${detail.lastRecordedOdometer.toLocaleString()} mi`
              : NOT_AVAILABLE
          }
          strong={false}
          caption={
            detail?.lastRecordedAt ? (
              <>
                as of <DateText value={detail.lastRecordedAt} />
              </>
            ) : undefined
          }
        />
      </div>
      <div className="flex h-10 items-center justify-end px-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onMoreDetails}
          className={`h-8 gap-2 rounded-md px-4 py-2.5 ${GHOST_BUTTON_LABEL_CLASS}`}
        >
          <IntlIcon name="maximize" className="h-4 w-4" />
          more details
        </Button>
      </div>
    </Panel>
  );
}

export function VehicleFacts({
  detail,
  vin,
  onMoreDetails,
}: {
  detail: VinDetail | null;
  vin: string;
  onMoreDetails: () => void;
}) {
  return (
    <div className="flex flex-col gap-6 px-4 lg:flex-row">
      <div className="grid w-full grid-rows-[auto_1fr] lg:w-[41%] lg:shrink-0">
        <SectionHeading>Purchase Information</SectionHeading>
        <PurchaseInformation detail={detail} />
      </div>
      <div className="flex-1">
        <SectionHeading>Vehicle Information</SectionHeading>
        <VehicleInformation
          detail={detail}
          vin={vin}
          onMoreDetails={onMoreDetails}
        />
      </div>
    </div>
  );
}
